import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  CBadge,
  CButton,
  CCard,
  CFormSelect,
  CLoadingButton,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react-pro'
import { cilCheckCircle, cilLink, cilPlus, cilSync, cilWarning, cilX } from '@coreui/icons'
import ToastComponent from 'src/components/common/TaostComponent'
import {
  ImportErrorAlert,
  InfoBox,
  MiniStepper,
  StatTile,
} from 'src/components/common/ImportWizard'
import PlayHQService from 'src/service/PlayHQService'
import {
  ACTIONS,
  ActionSelect,
  BulkBar,
  CHECK_CELL_STYLE,
  BulkSelect,
  FilterPills,
  LoadingBlock,
  RecordSelect,
  RowCheckbox,
  SelectAllCheckbox,
  duplicateTargets,
  errorsOf,
  reportBulk,
  rowStyle,
  tableHeadStyle,
  useRowSelection,
} from './playhqShared'

const STEPS = ['Validate', 'Review & Import']

// A match already imported can only be updated from PlayHQ or left alone.
const allowedActions = (row) =>
  row.imported ? ['update', 'skip'] : ['create', 'link', 'update', 'skip']

export const fixtureLabel = (f) =>
  `${f.round_number ? `R${f.round_number} · ` : ''}${f.team_name || 'Team'} vs ${
    f.opposition_club || 'TBC'
  } · ${f.start_date}${Number(f.match_type) === 28 ? ' · 2-Day' : ''}`

/**
 * Picks one of our fixtures, grouped by round. The round this PlayHQ match falls in (when the
 * game has one) is listed first, so the likely fixture is near the top.
 */
export const FixtureSelect = ({ value, onChange, fixtures, roundId, placeholder, invalid }) => {
  const groups = new Map()
  fixtures.forEach((f) => {
    const key = f.round_id && f.round_number ? String(f.round_id) : ''
    if (!groups.has(key)) groups.set(key, { number: f.round_number, fixtures: [] })
    groups.get(key).fixtures.push(f)
  })

  const ordered = [...groups.entries()].sort(([keyA, a], [keyB, b]) => {
    if (roundId && keyA === String(roundId)) return -1
    if (roundId && keyB === String(roundId)) return 1
    if (!keyA) return 1
    if (!keyB) return -1
    return Number(a.number) - Number(b.number)
  })

  return (
    <CFormSelect
      size="sm"
      value={value || ''}
      invalid={invalid}
      onChange={(e) => onChange(e.target.value)}
      style={{ minWidth: 240 }}
    >
      <option value="">{placeholder}</option>
      {ordered.map(([key, group]) => (
        <optgroup
          key={key || 'none'}
          label={
            key
              ? `Round ${group.number}${
                  roundId && key === String(roundId) ? ' - this match’s round' : ''
                }`
              : 'No round'
          }
        >
          {group.fixtures.map((f) => (
            <option key={f.id} value={f.id}>
              {fixtureLabel(f)}
            </option>
          ))}
        </optgroup>
      ))}
    </CFormSelect>
  )
}

const teamLabel = (t) => `${t.name}${t.grade ? ` (${t.grade})` : ''}`

export const STATUS_COLOR = {
  COMPLETED: 'success',
  ABANDONED: 'secondary',
  CANCELLED: 'secondary',
  IN_PROGRESS: 'info',
  UPCOMING: 'primary',
}

export const formatDate = (date, time) => {
  if (!date) return '—'
  const d = new Date(`${date}T00:00:00`)
  const day = Number.isNaN(d.getTime())
    ? date
    : d.toLocaleDateString(undefined, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
  return time ? `${day}, ${time}` : day
}

// One of our fixtures that is probably this match: same team, same start date, not yet linked.
const sameDayFixture = (item, ourFixtures, teamId) =>
  teamId
    ? ourFixtures.find(
        (f) =>
          !f.playhq_id && String(f.team) === String(teamId) && f.start_date === item.start_date,
      )
    : null

/**
 * PlayHQ matches → our fixtures. Each match is created as a new fixture, linked to one the admin
 * already made (optionally taking PlayHQ's date, venue and opponent), or skipped. A fixture's
 * round comes from its start date, as it does when a fixture is added by hand, so a match that
 * no round covers cannot be created until the rounds are set up.
 */
const FixturesImporter = ({ scope, teamIds, onImported }) => {
  const [data, setData] = useState(null)
  const [rows, setRows] = useState([])
  const [loadErrors, setLoadErrors] = useState([])
  const [step, setStep] = useState(1)
  const [filter, setFilter] = useState('all')
  const [importing, setImporting] = useState(false)
  const [importErrors, setImportErrors] = useState([])
  const [result, setResult] = useState(null)
  const selection = useRowSelection()
  const clearSelection = selection.clear

  const load = useCallback(() => {
    setData(null)
    setLoadErrors([])
    setResult(null)
    setImportErrors([])
    setStep(1)
    clearSelection()
    PlayHQService.preview('fixtures', { scope, team_ids: teamIds })
      .then((res) => {
        if (!res.success) {
          setLoadErrors(errorsOf(res))
          return
        }
        setData(res.data)
        setRows(
          (res.data.items || []).map((item) => {
            const teamId = item.team_id ? String(item.team_id) : ''
            if (item.fixture_id) {
              return {
                key: item.match_id,
                item,
                action: 'skip',
                team_id: teamId,
                target_id: String(item.fixture_id),
                imported: true,
              }
            }
            const candidate = sameDayFixture(item, res.data.our_fixtures || [], teamId)
            return {
              key: item.match_id,
              item,
              action: candidate ? 'link' : 'create',
              team_id: teamId,
              target_id: candidate ? String(candidate.id) : '',
              imported: false,
            }
          }),
        )
      })
      .catch(() => setLoadErrors(['PlayHQ fixtures could not be loaded. Please try again.']))
  }, [scope, teamIds, clearSelection])

  useEffect(() => {
    load()
  }, [load])

  const updateRow = (key, changes) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...changes } : row)))

  // Applies a change to every ticked row; `changesFor` returns null where it does not apply.
  const applyBulk = (changesFor, what) => {
    let applied = 0
    let refused = 0
    const next = rows.map((row) => {
      if (!selection.selected.has(row.key)) return row
      const changes = changesFor(row)
      if (!changes) {
        refused++
        return row
      }
      applied++
      return { ...row, ...changes }
    })
    setRows(next)
    reportBulk(applied, refused, what)
  }

  const checked = useMemo(() => {
    const dupes = duplicateTargets(rows)
    return rows.map((row) => {
      let problem = null
      const noRound = `No round in your game covers ${row.item.start_date}. Import rounds on the Rounds tab (or add one by hand) first, or link it to an existing fixture.`
      if (row.action === 'create') {
        if (!row.team_id) problem = 'Choose which of your teams played this match.'
        else if (!row.item.round_id) problem = noRound
      } else if (row.action === 'link' || row.action === 'update') {
        if (!row.target_id) problem = 'Choose which of your fixtures this match is.'
        else if (dupes.has(String(row.target_id)))
          problem = 'Another match is linked to the same fixture.'
        else if (row.action === 'update' && !row.item.round_id) problem = noRound
      }
      return {
        ...row,
        problem,
        tone: row.action === 'skip' ? 'skipped' : problem ? 'attention' : 'ready',
      }
    })
  }, [rows])

  const counts = useMemo(() => {
    const c = {
      all: checked.length,
      attention: 0,
      create: 0,
      link: 0,
      update: 0,
      skip: 0,
      imported: 0,
    }
    checked.forEach((row) => {
      if (row.tone === 'attention') c.attention++
      if (row.imported) c.imported++
      c[row.action]++
    })
    return c
  }, [checked])

  const runImport = async () => {
    setImporting(true)
    setImportErrors([])
    try {
      const payload = checked
        .filter((row) => row.action !== 'skip')
        .map((row) => ({
          match_id: row.item.match_id,
          action: row.action,
          team_id: row.team_id || null,
          fixture_id: row.target_id || null,
        }))
      const res = await PlayHQService.runImport('fixtures', { rows: payload })
      if (res.success) {
        ToastComponent(res.message || 'Fixtures imported.', 'success')
        setResult(res.message)
        if (onImported) onImported()
      } else {
        setImportErrors(errorsOf(res))
      }
    } catch (e) {
      setImportErrors(['The import could not be run. Please try again.'])
    } finally {
      setImporting(false)
    }
  }

  if (loadErrors.length) {
    return (
      <>
        <ImportErrorAlert errors={loadErrors} title="PlayHQ data could not be loaded:" />
        <CButton color="primary" variant="outline" onClick={load}>
          Try again
        </CButton>
      </>
    )
  }
  if (!data) return <LoadingBlock text="Loading matches from PlayHQ…" />
  if (result) {
    return (
      <>
        <InfoBox color="success">{result}</InfoBox>
        <CButton color="primary" variant="outline" onClick={load}>
          Review fixtures again
        </CButton>
      </>
    )
  }
  if (!rows.length)
    return <InfoBox color="light">PlayHQ has no matches for your club here.</InfoBox>

  const ourFixtures = data.our_fixtures || []
  const ourTeams = data.our_teams || []
  const fixtureById = (id) => ourFixtures.find((f) => String(f.id) === String(id))
  const teamById = (id) => ourTeams.find((t) => String(t.id) === String(id))

  const visible = checked.filter((row) => {
    if (filter === 'attention') return row.tone === 'attention'
    if (filter === 'create') return row.action === 'create'
    if (filter === 'existing') return row.action === 'link' || row.action === 'update'
    if (filter === 'skip') return row.action === 'skip'
    return true
  })

  const matchCell = (row) => (
    <>
      <div className="fw-semibold">
        {row.item.round_short || row.item.round_name} · {row.item.team_name} vs {row.item.opponent}
      </div>
      <div className="small text-medium-emphasis">
        {formatDate(row.item.start_date, row.item.start_time)}
        {row.item.end_date !== row.item.start_date && ` → ${formatDate(row.item.end_date)}`}
      </div>
      <div className="small text-medium-emphasis">{row.item.venue}</div>
      <div className="d-flex gap-1 mt-1 flex-wrap">
        <CBadge color={STATUS_COLOR[row.item.status] || 'secondary'} className="fw-normal">
          {row.item.status}
        </CBadge>
        <CBadge color="light" textColor="dark" className="fw-normal">
          {row.item.match_type_name}
        </CBadge>
        {row.imported && (
          <CBadge color="success" shape="rounded-pill" className="fw-normal">
            Already imported
          </CBadge>
        )}
        {row.item.round_number ? (
          <CBadge color="light" textColor="dark" className="fw-normal">
            Your round {row.item.round_number}
          </CBadge>
        ) : (
          <CBadge color="warning" className="fw-normal">
            No round
          </CBadge>
        )}
      </div>
      {row.problem && <div className="small text-warning mt-1">{row.problem}</div>}
    </>
  )

  const renderValidate = () => (
    <>
      <InfoBox color="light">
        Create each PlayHQ match as a fixture, or link it to a fixture you already made. Link keeps
        your fixture as it is; Link &amp; update takes PlayHQ&rsquo;s dates, venue and opponent. A
        fixture&rsquo;s round comes from its start date, as when you add one by hand.
      </InfoBox>
      <div className="d-flex gap-3 flex-wrap mb-3">
        <StatTile
          label="Ready"
          value={counts.all - counts.attention - counts.skip}
          tone="success"
          icon={cilCheckCircle}
        />
        <StatTile
          label="Needs attention"
          value={counts.attention}
          tone="warning"
          icon={cilWarning}
        />
        <StatTile label="Already imported" value={counts.imported} tone="info" icon={cilLink} />
      </div>
      <FilterPills
        value={filter}
        onChange={setFilter}
        filters={[
          { key: 'all', label: 'All', count: counts.all },
          { key: 'attention', label: 'Needs attention', count: counts.attention },
          { key: 'create', label: 'Create new', count: counts.create },
          { key: 'existing', label: 'Link to existing', count: counts.link + counts.update },
          { key: 'skip', label: 'Skipped', count: counts.skip },
        ]}
      />
      <BulkBar count={selection.selected.size} onClear={selection.clear}>
        <BulkSelect
          placeholder="Set action…"
          options={Object.entries(ACTIONS).map(([value, a]) => ({ value, label: a.label }))}
          onPick={(action) =>
            applyBulk(
              (row) => (allowedActions(row).includes(action) ? { action } : null),
              `"${ACTIONS[action].label}"`,
            )
          }
        />
        <BulkSelect
          placeholder="Set team (new fixtures)…"
          options={ourTeams.map((t) => ({ value: String(t.id), label: teamLabel(t) }))}
          onPick={(team_id) =>
            applyBulk((row) => (row.action === 'create' ? { team_id } : null), 'Team')
          }
        />
      </BulkBar>
      <CCard className="border-secondary-subtle rounded-4 overflow-hidden mb-3">
        <div className="table-responsive">
          <CTable align="middle" hover className="mb-0">
            <CTableHead className="align-middle" style={tableHeadStyle}>
              <CTableRow>
                <CTableHeaderCell className="ps-3" style={CHECK_CELL_STYLE}>
                  <SelectAllCheckbox keys={visible.map((row) => row.key)} selection={selection} />
                </CTableHeaderCell>
                <CTableHeaderCell>PlayHQ match</CTableHeaderCell>
                <CTableHeaderCell>Action</CTableHeaderCell>
                <CTableHeaderCell>Your team / fixture</CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {visible.map((row) => (
                <CTableRow key={row.key} style={rowStyle(row.tone)}>
                  <CTableDataCell className="ps-3">
                    <RowCheckbox
                      checked={selection.selected.has(row.key)}
                      onChange={() => selection.toggle(row.key)}
                      title={`Select ${row.item.round_short} ${row.item.team_name} vs ${row.item.opponent}`}
                    />
                  </CTableDataCell>
                  <CTableDataCell>{matchCell(row)}</CTableDataCell>
                  <CTableDataCell>
                    <ActionSelect
                      value={row.action}
                      onChange={(action) => updateRow(row.key, { action })}
                      allowed={allowedActions(row)}
                    />
                  </CTableDataCell>
                  <CTableDataCell>
                    {row.action === 'create' && (
                      <RecordSelect
                        value={row.team_id}
                        onChange={(team_id) => updateRow(row.key, { team_id })}
                        records={ourTeams}
                        getLabel={teamLabel}
                        placeholder="Choose team…"
                        invalid={!row.team_id}
                      />
                    )}
                    {(row.action === 'link' || row.action === 'update') &&
                      (row.imported ? (
                        <span className="small">
                          {fixtureById(row.target_id)
                            ? fixtureLabel(fixtureById(row.target_id))
                            : '—'}
                        </span>
                      ) : (
                        <FixtureSelect
                          value={row.target_id}
                          onChange={(target_id) => updateRow(row.key, { target_id })}
                          fixtures={ourFixtures.filter(
                            (f) => !f.playhq_id || String(f.id) === String(row.target_id),
                          )}
                          roundId={row.item.round_id}
                          placeholder="Choose fixture…"
                          invalid={!row.target_id}
                        />
                      ))}
                    {row.action === 'skip' && (
                      <span className="small text-medium-emphasis">
                        {row.imported && fixtureById(row.target_id)
                          ? fixtureLabel(fixtureById(row.target_id))
                          : '—'}
                      </span>
                    )}
                  </CTableDataCell>
                </CTableRow>
              ))}
            </CTableBody>
          </CTable>
        </div>
      </CCard>
      <div className="d-flex justify-content-end gap-2">
        <CButton color="secondary" variant="outline" onClick={load}>
          Reload from PlayHQ
        </CButton>
        <CButton
          color="primary"
          disabled={counts.attention > 0 || counts.all === counts.skip}
          onClick={() => setStep(2)}
        >
          Review {counts.all - counts.skip}{' '}
          {counts.all - counts.skip === 1 ? 'fixture' : 'fixtures'}
        </CButton>
      </div>
    </>
  )

  const renderReview = () => {
    const toImport = checked.filter((row) => row.action !== 'skip')
    return (
      <>
        <div className="d-flex gap-3 flex-wrap mb-3">
          <StatTile label="Create new" value={counts.create} tone="success" icon={cilPlus} />
          <StatTile label="Link to existing" value={counts.link} tone="info" icon={cilLink} />
          <StatTile
            label="Update from PlayHQ"
            value={counts.update}
            tone="warning"
            icon={cilSync}
          />
          <StatTile label="Skipped" value={counts.skip} tone="secondary" icon={cilX} />
        </div>
        <CCard className="border-secondary-subtle rounded-4 overflow-hidden mb-3">
          <div className="table-responsive">
            <CTable align="middle" className="mb-0">
              <CTableHead className="align-middle" style={tableHeadStyle}>
                <CTableRow>
                  <CTableHeaderCell className="ps-3">PlayHQ match</CTableHeaderCell>
                  <CTableHeaderCell>What happens</CTableHeaderCell>
                  <CTableHeaderCell>Your fixture</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {toImport.map((row) => (
                  <CTableRow key={row.key}>
                    <CTableDataCell className="ps-3">
                      {row.item.round_short} · {row.item.team_name} vs {row.item.opponent}
                      <div className="small text-medium-emphasis">
                        {formatDate(row.item.start_date, row.item.start_time)}
                      </div>
                    </CTableDataCell>
                    <CTableDataCell>{ACTIONS[row.action].label}</CTableDataCell>
                    <CTableDataCell>
                      {row.action === 'create'
                        ? `New: ${teamById(row.team_id)?.name || ''} vs ${row.item.opponent} · ${
                            row.item.match_type_name
                          } · your round ${row.item.round_number}`
                        : fixtureById(row.target_id)
                        ? fixtureLabel(fixtureById(row.target_id))
                        : '—'}
                    </CTableDataCell>
                  </CTableRow>
                ))}
              </CTableBody>
            </CTable>
          </div>
        </CCard>
        <ImportErrorAlert errors={importErrors} />
        <div className="d-flex justify-content-end gap-2">
          <CButton color="secondary" variant="outline" onClick={() => setStep(1)}>
            Back
          </CButton>
          <CLoadingButton color="primary" loading={importing} onClick={runImport}>
            Import {toImport.length} {toImport.length === 1 ? 'fixture' : 'fixtures'}
          </CLoadingButton>
        </div>
      </>
    )
  }

  return (
    <>
      <MiniStepper steps={STEPS} current={step} maxReached={step} onStepClick={setStep} />
      {step === 1 ? renderValidate() : renderReview()}
    </>
  )
}

export default FixturesImporter
